import { useCallback, useEffect, useRef, useState } from 'react';
import type { Finding } from '../../shared/types.js';

export class ApiError extends Error {
  constructor(public status: number, message: string, public findings?: Finding[], public details?: string[]) {
    super(message);
  }
}

/**
 * Where the API lives. Empty in a browser, where the same server serves both the
 * client and the API; a full origin in a native shell, whose pages are loaded
 * from the device rather than from us.
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

/** True inside a Capacitor WebView, which is the only place we hold a token. */
export function isNativeShell(): boolean {
  if (typeof window === 'undefined') return false;
  return 'Capacitor' in window
    || window.location.protocol === 'capacitor:'
    || window.location.protocol === 'ionic:';
}

/**
 * A browser keeps its session in an httpOnly cookie, which no script can read
 * and therefore no injected script can steal. A native shell has no cookie jar
 * worth trusting, so there — and only there — the same token is held by us and
 * sent as a bearer header.
 */
const TOKEN_KEY = 'flightslots.token';

function storedToken(): string | null {
  if (!isNativeShell()) return null;
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function setSessionToken(token: string | undefined): void {
  if (!isNativeShell() || !token) return;
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* storage unavailable */ }
}

export function clearSessionToken(): void {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* storage unavailable */ }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = storedToken();
  const res = await fetch(`${API_BASE}/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const payload = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, payload?.error ?? res.statusText, payload?.findings, payload?.details);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  request: <T>(method: string, path: string, body?: unknown) => request<T>(method, path, body),
};

export interface Query<T> {
  data: T | undefined;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Minimal data hook. `path` doubles as the cache key; passing null skips the
 * fetch, which is how screens wait for a dependency before loading.
 */
export function useQuery<T>(path: string | null, deps: unknown[] = []): Query<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [nonce, setNonce] = useState(0);
  // Keeps a slow response from overwriting a newer one.
  const latest = useRef(0);

  useEffect(() => {
    if (!path) { setLoading(false); return; }
    const ticket = ++latest.current;
    setLoading(true);
    api.get<T>(path)
      .then((result) => {
        if (ticket !== latest.current) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (ticket !== latest.current) return;
        setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (ticket === latest.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

export function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function hours(value: number): string {
  return value.toFixed(1);
}
